import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";

const ReviewSubmitTab = ({
  formData,
  updateFormData,
  onPrevious,
  onSubmit,
  submitting,
  escalations
}) => {
  // Guard against undefined formData
  if (!formData || !formData.basicInfo || !formData.leaseTerms || !formData.financialTerms || !formData.billingCycle) {
    return (
      <Card title="Review & Submit" className="mb-4">
        <div className="p-4 text-center">
          <i className="ti ti-loader-2 text-4xl text-primary animate-spin"></i>
          <p className="mt-2 text-600">Loading...</p>
        </div>
      </Card>
    );
  }

  const { basicInfo, leaseTerms, financialTerms, billingCycle } = formData;

  // Get display data (the data is already resolved in the other tabs)
  const selectedUnit = { name: basicInfo.unitName || "Selected Unit" };
  const selectedTenant = { firstName: basicInfo.tenantName?.split(' ')[0] || "Selected", lastName: basicInfo.tenantName?.split(' ')[1] || "Tenant" };
  const selectedLandlord = { firstName: basicInfo.landlordName?.split(' ')[0] || "Selected", lastName: basicInfo.landlordName?.split(' ')[1] || "Landlord" };

  const formatCurrency = (amount) => {
    const currencySymbol = basicInfo.selectedCurrency?.currencySymbol || '';
    return `${currencySymbol} ${parseFloat(amount || 0).toLocaleString()}`;
  };

  const formatDate = (date) => {
    if (!date) return "Not set";
    return new Date(date).toLocaleDateString();
  };

  const calculateTotalDeposit = () => {
    const monthlyRent = parseFloat(financialTerms.monthlyRent) || 0;
    const depositMonths = parseInt(financialTerms.depositMonths) || 1;
    const manualDeposit = parseFloat(financialTerms.securityDeposit) || 0;

    return financialTerms.manualDepositEntry ? manualDeposit : monthlyRent * depositMonths;
  };

  const escalationActionTemplate = (rowData) => {
    return (
      <Tag
        value={rowData.status}
        severity={rowData.status === 'scheduled' ? 'info' : 'success'}
      />
    );
  };

  return (
    <div className="row">
      {/* Basic Information Summary */}
      <div className="col-12 mb-4">
        <div className="card">
          <div className="card-body">
            <h6 className="card-title text-primary">Basic Information</h6>
            <div className="row">
              <div className="col-md-6">
                <p className="mb-2"><strong>Unit:</strong> {selectedUnit?.name || "Not selected"}</p>
                <p className="mb-2"><strong>Unit Type:</strong> {basicInfo.unitType || "N/A"}</p>
                <p className="mb-2"><strong>Status:</strong> <Tag value={basicInfo.status} severity="info" /></p>
              </div>
              <div className="col-md-6">
                <p className="mb-2"><strong>Tenant:</strong> {selectedTenant ? `${selectedTenant.firstName} ${selectedTenant.lastName}` : "Not selected"}</p>
                <p className="mb-2"><strong>Currency:</strong> {basicInfo.selectedCurrency?.currencyName || "Not selected"}</p>
                <p className="mb-2"><strong>New Tenant:</strong> {basicInfo.isNewTenant ? "Yes" : "No"}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lease Terms Summary */}
      <div className="col-12 mb-4">
        <div className="card">
          <div className="card-body">
            <h6 className="card-title text-primary">Lease Terms</h6>
            <div className="row">
              <div className="col-md-6">
                <p className="mb-2"><strong>Start Date:</strong> {formatDate(leaseTerms.startDate)}</p>
                <p className="mb-2"><strong>End Date:</strong> {formatDate(leaseTerms.endDate)}</p>
              </div>
              <div className="col-md-6">
                <p className="mb-2"><strong>Duration:</strong> {leaseTerms.leaseDuration || "0"} months</p>
                <p className="mb-2"><strong>Auto Renewal:</strong> {leaseTerms.autoRenewal ? "Enabled" : "Disabled"}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Financial Terms Summary */}
      <div className="col-12 mb-4">
        <div className="card">
          <div className="card-body">
            <h6 className="card-title text-primary">Financial Terms</h6>
            <div className="row">
              <div className="col-md-6">
                <p className="mb-2"><strong>Monthly Rent:</strong> {formatCurrency(financialTerms.monthlyRent)}</p>
                <p className="mb-2"><strong>Security Deposit:</strong> {formatCurrency(calculateTotalDeposit())}</p>
                <p className="mb-2"><strong>Balance B/F:</strong> {formatCurrency(financialTerms.balanceBroughtForward)}</p>
              </div>
              <div className="col-md-6">
                <p className="mb-2"><strong>Payment Due:</strong> {financialTerms.paymentDueDate || "Not set"} of each month</p>
                <p className="mb-2"><strong>Deposit Method:</strong> {financialTerms.manualDepositEntry ? "Custom amount" : `${financialTerms.depositMonths || 1} months`}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Methods Summary */}
      <div className="col-12 mb-4">
        <div className="card">
          <div className="card-body">
            <h6 className="card-title text-primary">Payment Methods</h6>
            {financialTerms.paymentMethods && financialTerms.paymentMethods.length > 0 ? (
              <div className="row">
                {financialTerms.paymentMethods.map((method, index) => (
                  <div key={index} className="col-md-6 mb-3">
                    <div className="bg-light p-2 rounded">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong>{method.type}</strong>
                        {method.isPrimary && <Tag value="Primary" severity="warning" />}
                      </div>

                      {method.type === "Bank Transfer" && (
                        <div>
                          {method.details?.bankDetailId ? (
                            <div>
                              <p className="mb-1 small"><strong>Bank:</strong> {method.details.bankName}</p>
                              <p className="mb-1 small"><strong>Account:</strong> {method.details.accountName}</p>
                              <p className="mb-0 small"><strong>Number:</strong> {method.details.accountNumber}</p>
                            </div>
                          ) : (
                            <div>
                              <p className="mb-1 small"><strong>Bank:</strong> {method.details?.bankName || "Not specified"}</p>
                              <p className="mb-0 small"><strong>Account:</strong> {method.details?.accountNumber || "Not specified"}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {method.type === "Mobile Money" && (
                        <div>
                          <p className="mb-1 small"><strong>Provider:</strong> {method.details?.provider || "Not specified"}</p>
                          <p className="mb-0 small"><strong>Phone:</strong> {method.details?.phoneNumber || "Not specified"}</p>
                        </div>
                      )}

                      {method.type === "Cash" && (
                        <div>
                          <p className="mb-0 small"><strong>Location:</strong> {method.details?.preferredCashLocation || "Not specified"}</p>
                        </div>
                      )}

                      {method.type === "Cheque" && (
                        <div>
                          <p className="mb-1 small"><strong>Bank:</strong> {method.details?.bankToDraft || "Not specified"}</p>
                          <p className="mb-0 small"><strong>Account:</strong> {method.details?.chequeAccountNumber || "Not specified"}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted">No payment methods configured</p>
            )}
          </div>
        </div>
      </div>

      {/* Billing Cycle Summary */}
      <div className="col-12 mb-4">
        <div className="card">
          <div className="card-body">
            <h6 className="card-title text-primary">Billing Cycle</h6>
            <div className="row">
              <div className="col-md-4">
                <p className="mb-2"><strong>Frequency:</strong> {billingCycle.billingFrequency || "Monthly"}</p>
              </div>
              <div className="col-md-4">
                <p className="mb-2"><strong>Next Invoice:</strong> {formatDate(billingCycle.nextInvoiceDate)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rent Escalations */}
      {escalations && escalations.length > 0 && (
        <div className="col-12 mb-4">
          <div className="card">
            <div className="card-body">
              <h6 className="card-title text-primary">Rent Escalations</h6>
              <DataTable value={escalations} className="p-datatable-sm">
                <Column field="effectiveDate" header="Effective Date" body={(rowData) => formatDate(rowData.effectiveDate)} />
                <Column field="type" header="Type" body={(rowData) => rowData.type === 'percentage' ? 'Percentage' : 'Fixed Amount'} />
                <Column field="value" header="Value" body={(rowData) => `${rowData.value}${rowData.type === 'percentage' ? '%' : ` ${basicInfo.selectedCurrency?.currencySymbol || ''}`}`} />
                <Column field="status" header="Status" body={escalationActionTemplate} />
              </DataTable>
            </div>
          </div>
        </div>
      )}

      {/* Landlord Information */}
      {selectedLandlord && (
        <div className="col-12 mb-4">
          <div className="card">
            <div className="card-body">
              <h6 className="card-title text-primary">Landlord Information</h6>
              <div className="row">
                <div className="col-md-4">
                  <p className="mb-2"><strong>Name:</strong> {selectedLandlord.firstName} {selectedLandlord.lastName}</p>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Additional Notes */}
      <div className="col-12 mb-4">
        <div className="card">
          <div className="card-body">
            <h6 className="card-title text-primary">Additional Notes (Optional)</h6>
            <InputTextarea
              value={formData.notes || ""}
              onChange={(e) => updateFormData.updateBasicInfo({ notes: e.target.value })}
              placeholder="Add any additional notes or special terms for this lease agreement..."
              rows={3}
              className="w-100"
            />
          </div>
        </div>
      </div>

      {/* Agreement Confirmation */}
      <div className="col-12 mb-4">
        <div className="card bg-light">
          <div className="card-body text-center">
            <h6 className="text-primary">Ready to Create Lease Agreement</h6>
            <p className="text-muted mb-0">
              Please review all information above. Once submitted, the lease agreement will be created and can be activated.
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="col-12 mt-4">
        <div className="d-flex justify-content-between">
          <Button
            label="Previous: Payment Methods"
            icon="ti ti-arrow-left"
            className="p-button-secondary"
            onClick={onPrevious}
            disabled={submitting}
          />
          <Button
            label={submitting ? "Creating Lease..." : "Create Lease Agreement"}
            icon={submitting ? "ti ti-loader-2" : "ti ti-check"}
            iconPos={submitting ? "left" : "right"}
            className="p-button-success p-button-lg"
            onClick={onSubmit}
            loading={submitting}
            disabled={submitting}
          />
        </div>
      </div>
    </div>
  );
};

export default ReviewSubmitTab;